import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, IconButton, InputAdornment, MenuItem, TextField } from '@mui/material';
import { ChevronRight as ChevronIcon, Visibility, VisibilityOff } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { authApi } from '../services/authApi';
import { OnboardingWizard } from '../components/onboarding';
import DeleteAccountDialog from '../components/settings/DeleteAccountDialog';
import FinMark from '../components/brand/FinMark';
import { SUPPORTED_CURRENCIES, getCurrencySymbol } from '../utils/currency.utils';
import { tokens } from '../theme/tokens';

const LANGUAGES = ['pt', 'en', 'es'];
const TIMEZONES = [
  'America/Sao_Paulo',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'Europe/London',
  'Europe/Paris',
  'Asia/Tokyo',
  'Australia/Sydney',
  'UTC',
];

/** One card of Ajustes: a title, a line on what it is for, and its content. */
const Section: React.FC<{ title: string; hint?: string; children: React.ReactNode }> = ({ title, hint, children }) => (
  <Box
    component='section'
    aria-label={title}
    sx={{
      bgcolor: tokens.surface,
      border: `1px solid ${tokens.line}`,
      borderRadius: `${tokens.radius.card}px`,
      p: { xs: 2.25, md: 3 },
      display: 'flex',
      flexDirection: 'column',
      gap: 2,
      minWidth: 0,
    }}
  >
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <Box component='h2' sx={{ m: 0, fontFamily: tokens.display, fontSize: 18, fontWeight: 650 }}>{title}</Box>
      {hint && <Box component='p' sx={{ m: 0, fontSize: 14, color: tokens.ink3, lineHeight: 1.5 }}>{hint}</Box>}
    </Box>
    {children}
  </Box>
);

type Feedback = { kind: 'success' | 'error'; text: string } | null;

const Status: React.FC<{ feedback: Feedback }> = ({ feedback }) =>
  feedback ? <Alert severity={feedback.kind} role={feedback.kind === 'error' ? 'alert' : 'status'}>{feedback.text}</Alert> : null;

/**
 * Ajustes: the profile, the preferences (language, time zone, currency),
 * Telegram, the password, the tutorial and deleting the account, one card each.
 */
const SettingsPage: React.FC = () => {
  const { t } = useTranslation('settings');
  const navigate = useNavigate();
  const { state: authState, applyUser } = useAuth();
  const { changeLanguage } = useLanguage();
  const user = authState.user;

  const [firstName, setFirstName] = useState(user?.firstName || '');
  const [lastName, setLastName] = useState(user?.lastName || '');
  const [language, setLanguage] = useState(user?.language || 'pt');
  const [timezone, setTimezone] = useState(user?.timezone || 'America/Sao_Paulo');
  const [currency, setCurrency] = useState(user?.defaultCurrency || 'BRL');
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [profileFeedback, setProfileFeedback] = useState<Feedback>(null);
  const [prefsFeedback, setPrefsFeedback] = useState<Feedback>(null);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswords, setShowPasswords] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [passwordFeedback, setPasswordFeedback] = useState<Feedback>(null);

  const [showTutorial, setShowTutorial] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const linked = Boolean(user?.isTelegramLinked);

  useEffect(() => {
    if (!user) return;
    setFirstName(user.firstName || '');
    setLastName(user.lastName || '');
    setLanguage(user.language || 'pt');
    setTimezone(user.timezone || 'America/Sao_Paulo');
    setCurrency(user.defaultCurrency || 'BRL');
  }, [user]);

  const profileChanged = firstName !== (user?.firstName || '') || lastName !== (user?.lastName || '');
  const prefsChanged = language !== user?.language || timezone !== user?.timezone || currency !== user?.defaultCurrency;
  const zones = timezone && !TIMEZONES.includes(timezone) ? [timezone, ...TIMEZONES] : TIMEZONES;

  // Saves go through the user the server returns: a failed refresh must never sign anyone out.
  const save = async (
    data: Parameters<typeof authApi.updateProfile>[0],
    setBusy: (busy: boolean) => void,
    setFeedback: (feedback: Feedback) => void,
    success: string,
    failure: string,
  ) => {
    setBusy(true);
    setFeedback(null);
    try {
      applyUser(await authApi.updateProfile(data));
      setFeedback({ kind: 'success', text: success });
    } catch (error: any) {
      setFeedback({ kind: 'error', text: error.response?.data?.message || failure });
    } finally {
      setBusy(false);
    }
  };

  const saveProfile = () => {
    if (!firstName.trim() || !lastName.trim()) {
      setProfileFeedback({ kind: 'error', text: t('profile.errorRequired') });
      return;
    }
    save({ firstName: firstName.trim(), lastName: lastName.trim() }, setSavingProfile, setProfileFeedback, t('profile.successMessage'), t('profile.errorGeneric'));
  };

  const savePreferences = () =>
    save({ language, timezone, defaultCurrency: currency }, setSavingPrefs, setPrefsFeedback, t('preferences.successMessage'), t('preferences.errorGeneric'));

  const changePassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordFeedback({ kind: 'error', text: t('password.errorRequired') });
      return;
    }
    if (newPassword.length < 8) {
      setPasswordFeedback({ kind: 'error', text: t('password.errorMinLength') });
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordFeedback({ kind: 'error', text: t('password.errorMismatch') });
      return;
    }
    setSavingPassword(true);
    setPasswordFeedback(null);
    try {
      await authApi.changePassword({ currentPassword, newPassword });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setPasswordFeedback({ kind: 'success', text: t('password.successMessage') });
    } catch (error: any) {
      setPasswordFeedback({ kind: 'error', text: error.response?.data?.message || t('password.errorGeneric') });
    } finally {
      setSavingPassword(false);
    }
  };

  const visibility = (
    <InputAdornment position='end'>
      <IconButton aria-label={showPasswords ? t('password.hide') : t('password.show')} onClick={() => setShowPasswords((shown) => !shown)} edge='end'>
        {showPasswords ? <VisibilityOff /> : <Visibility />}
      </IconButton>
    </InputAdornment>
  );

  const saveButton = (label: string, busy: boolean, disabled: boolean, onClick: () => void) => (
    <Button variant='contained' onClick={onClick} disabled={busy || disabled} startIcon={busy ? <CircularProgress size={16} color='inherit' /> : undefined} sx={{ alignSelf: 'flex-start', minHeight: 44 }}>
      {label}
    </Button>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, md: 2.5 } }}>
      <Box component='h1' sx={{ m: 0, fontFamily: tokens.display, fontSize: { xs: 26, md: 30 }, fontWeight: 700, letterSpacing: '-0.025em' }}>
        {t('title')}
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: { xs: 2, md: 2.5 }, alignItems: 'start' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, md: 2.5 } }}>
          <Section title={t('profile.title')}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 1.75 }}>
              <TextField label={t('profile.firstName')} value={firstName} onChange={(event) => setFirstName(event.target.value)} required />
              <TextField label={t('profile.lastName')} value={lastName} onChange={(event) => setLastName(event.target.value)} required />
            </Box>
            <TextField label={t('profile.email')} value={user?.email || ''} disabled helperText={t('profile.emailHelper')} />
            <Status feedback={profileFeedback} />
            {saveButton(t('profile.saveButton'), savingProfile, !profileChanged, saveProfile)}
          </Section>

          <Section title={t('preferences.title')} hint={t('preferences.hint')}>
            <TextField
              select
              label={t('profile.language')}
              value={language}
              onChange={(event) => {
                setLanguage(event.target.value);
                changeLanguage(event.target.value);
              }}
            >
              {LANGUAGES.map((code) => <MenuItem key={code} value={code}>{t(`languages.${code}`)}</MenuItem>)}
            </TextField>
            <TextField select label={t('profile.timezone')} value={timezone} onChange={(event) => setTimezone(event.target.value)}>
              {zones.map((zone) => (
                <MenuItem key={zone} value={zone}>{t(`timezones.${zone}`, { defaultValue: zone.replace(/_/g, ' ') })}</MenuItem>
              ))}
            </TextField>
            <TextField select label={t('currency.defaultCurrency')} value={currency} onChange={(event) => setCurrency(event.target.value)} helperText={t('preferences.currencyHint')}>
              {SUPPORTED_CURRENCIES.map((code) => <MenuItem key={code} value={code}>{`${getCurrencySymbol(code)} · ${code}`}</MenuItem>)}
            </TextField>
            <Status feedback={prefsFeedback} />
            {saveButton(t('preferences.saveButton'), savingPrefs, !prefsChanged, savePreferences)}
          </Section>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, md: 2.5 } }}>
          <Section title={t('telegram.title')} hint={linked ? undefined : t('telegram.hint')}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.75, borderRadius: '16px', bgcolor: linked ? tokens.accentSoft : tokens.bg }}>
              <FinMark size={36} />
              <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                <Box component='strong' sx={{ fontSize: 15 }}>{linked ? t('telegram.connected') : t('telegram.notConnected')}</Box>
                <Box component='span' sx={{ fontSize: 13.5, color: tokens.ink2, overflowWrap: 'anywhere' }}>
                  {linked && user?.telegramUsername ? `@${user.telegramUsername}` : linked ? t('telegram.connectedHint') : t('telegram.notConnectedHint')}
                </Box>
              </Box>
            </Box>
            <Button
              variant={linked ? 'outlined' : 'contained'}
              endIcon={<ChevronIcon />}
              onClick={() => navigate('/settings/telegram')}
              sx={{ alignSelf: 'flex-start', minHeight: 44 }}
            >
              {linked ? t('telegram.manageButton') : t('telegram.connectButton')}
            </Button>
          </Section>

          <Section title={t('password.title')}>
            <TextField
              label={t('password.currentPassword')}
              type={showPasswords ? 'text' : 'password'}
              value={currentPassword}
              onChange={(event) => setCurrentPassword(event.target.value)}
              autoComplete='current-password'
              InputProps={{ endAdornment: visibility }}
            />
            <TextField
              label={t('password.newPassword')}
              type={showPasswords ? 'text' : 'password'}
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
              autoComplete='new-password'
              helperText={t('password.helper')}
            />
            <TextField
              label={t('password.confirmPassword')}
              type={showPasswords ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              autoComplete='new-password'
            />
            <Status feedback={passwordFeedback} />
            {saveButton(t('password.changeButton'), savingPassword, false, changePassword)}
          </Section>

          <Section title={t('tutorial.title')} hint={t('tutorial.description')}>
            <Button variant='outlined' onClick={() => setShowTutorial(true)} sx={{ alignSelf: 'flex-start', minHeight: 44 }}>
              {t('tutorial.button')}
            </Button>
          </Section>

          <Section title={t('deleteAccount.title')} hint={t('deleteAccount.description')}>
            <Button
              variant='outlined'
              onClick={() => setShowDelete(true)}
              sx={{ alignSelf: 'flex-start', minHeight: 44, color: tokens.danger, borderColor: tokens.danger }}
            >
              {t('deleteAccount.button')}
            </Button>
          </Section>
        </Box>
      </Box>

      {showTutorial && <OnboardingWizard open={showTutorial} onComplete={() => setShowTutorial(false)} />}
      <DeleteAccountDialog open={showDelete} onClose={() => setShowDelete(false)} />
    </Box>
  );
};

export default SettingsPage;
